import { initializer, inject, injectable } from "@launchtray/tsyringe-async";
import { IPlayerModel } from "by/gamefactory/cityversetycoon/scenes/common/models/IPlayerModel";
import { ICanvasScaler } from "../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../shared/async/PromiseCompletionSource";
import { Viewable } from "../../../../scene/viewable/Viewable";
import { IFirstTimeExperienceController } from "../../common/controller/IFirstTimeExperienceController";
import { IFirstTimeExperienceModel } from "../../common/models/IFirstTimeExperienceModel";
import { CommonResourcesPackage } from "../../common/resources/CommonResourcesPackage";
import { SoundsResourcePackageWrapper } from "../../common/resources/SoundsResourcePackageWrapper";
import { TopPanel } from "../../game/components/toppanel/TopPanel";
import { VisitResourcePackage } from "./resources/VisitResourcePackage";
import { VisitPopupView } from "./view/VisitPopupView";
import { UserProfileResourcePackage } from "../userprofile/resources/UserProfileResourcePackage";
import { IVisitPopupController } from "./controller/IVisitPopupController";
import { NoCacheDynamicPackage } from "../../common/resources/NoCacheDynamicPackage";
import { VisitAction } from "../../game/flow/impl/diceActions/visitAction/VisitAction";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";

@injectable()
export class VisitPopupScene extends Viewable<void> {
    private readonly commonResources: CommonResourcesPackage;
    private readonly visitResources: VisitResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly visitPopupController: IVisitPopupController;
    private readonly playerModel: IPlayerModel;
    private readonly canvasScaler: ICanvasScaler;
    private readonly topPanel: TopPanel;
    private readonly firstTimeExperienceModel: IFirstTimeExperienceModel;
    private readonly firstTimeExperienceController: IFirstTimeExperienceController;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly visitDynamicResources: NoCacheDynamicPackage;
    private readonly action: VisitAction;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private isFirstShow: boolean;

    public constructor(
        commonResources: CommonResourcesPackage,
        soundResources: SoundsResourcePackageWrapper,
        visitResources: VisitResourcePackage,
        @inject("IVisitPopupController") visitPopupController: IVisitPopupController,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("ICanvasScaler") canvasScaler: ICanvasScaler,
        action: VisitAction,
        topPanel: TopPanel,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("IFirstTimeExperienceModel") firstTimeExperienceModel: IFirstTimeExperienceModel,
        @inject("IFirstTimeExperienceController") firstTimeExperienceController: IFirstTimeExperienceController,
        userProfileResources: UserProfileResourcePackage
    ) {
        super();
        this.visitResources = visitResources;
        this.commonResources = commonResources;
        this.soundResources = soundResources;
        this.canvasScaler = canvasScaler;
        this.action = action;
        this.visitPopupController = visitPopupController;
        this.playerModel = playerModel;
        this.isFirstShow = false;
        this.topPanel = topPanel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.firstTimeExperienceModel = firstTimeExperienceModel;
        this.firstTimeExperienceController = firstTimeExperienceController;
        this.userProfileResources = userProfileResources;
        this.visitDynamicResources = new NoCacheDynamicPackage("visit");
    }

    @initializer()
    public async initialization(): Promise<void> {
        this.topPanel.interactiveChildren = false;
        await this.asyncOperationNotification.autoShowHide(this.loadVisitDynamicResources());
        if (this.firstTimeExperienceModel.tooltipsInfo.value.visit) {
            await this.firstTimeExperienceController.sendShownTooltip({ tooltips: { visit: false } });
            this.isFirstShow = true;
        }
        const pcs = new PromiseCompletionSource<void>();
        this.view = new VisitPopupView(
            this.commonResources,
            this.soundResources,
            this.visitResources,
            this.visitPopupController,
            this.playerModel,
            this.canvasScaler,
            this.isFirstShow,
            pcs,
            this.topPanel,
            this.userProfileResources,
            this.action,
            this.visitDynamicResources
        );
        pcs.result.then(() => {
            this.topPanel.interactiveChildren = true;
            this.operationControls.complete();
            this.visitResources.destroy();
            this.userProfileResources.destroy();
            this.visitDynamicResources.destroy();
        }, (reason: any) => {
            this.topPanel.interactiveChildren = true;
            this.visitResources.destroy();
            this.userProfileResources.destroy();
            this.visitDynamicResources.destroy();
            this.operationControls.error(reason);
        });
    }

    private async loadVisitDynamicResources(): Promise<void> {
        this.visitDynamicResources.add(this.action.diceRollVisitCard.building.imageUrl);
        this.visitDynamicResources.add(this.action.diceRollVisitCard.guest.avatar);
        await this.visitDynamicResources.load();
    }
}
